import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Switch,
  FormControlLabel,
  alpha,
  useTheme
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as OfferIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ActiveIcon,
  Pause as PauseIcon,
  People as PeopleIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import CreatePromotionModal from "./CreatePromotionalModal";

// Mock data - moved inside component or keep outside but use state
const initialPromotions = [
  {
    id: 1,
    name: "Spring Cleaning Special",
    code: "SPRING25",
    type: "Percentage",
    discount: 25,
    minAmount: 100,
    maxUses: 100,
    used: 45,
    status: "Active",
    startDate: "2024-03-01",
    endDate: "2024-04-30",
    audience: "All Customers",
    description: "25% off all cleaning services this spring"
  },
  {
    id: 2,
    name: "First Time Customer",
    code: "WELCOME15",
    type: "Fixed",
    discount: 15,
    minAmount: 50,
    maxUses: 500,
    used: 234,
    status: "Active",
    startDate: "2024-01-01",
    endDate: "2024-12-31",
    audience: "New Customers",
    description: "$15 off for first-time customers"
  },
  {
    id: 3,
    name: "Referral Bonus",
    code: "REFER20",
    type: "Percentage",
    discount: 20,
    minAmount: 0,
    maxUses: 1000,
    used: 89,
    status: "Active",
    startDate: "2024-02-01",
    endDate: "2024-06-30",
    audience: "Existing Customers",
    description: "20% off when you refer a friend"
  },
  {
    id: 4,
    name: "Holiday Special",
    code: "HOLIDAY30",
    type: "Percentage",
    discount: 30,
    minAmount: 150,
    maxUses: 200,
    used: 0,
    status: "Scheduled",
    startDate: "2024-12-01",
    endDate: "2024-12-31",
    audience: "All Customers",
    description: "30% off during holiday season"
  },
  {
    id: 5,
    name: "Deep Clean Discount",
    code: "DEEP20",
    type: "Fixed",
    discount: 20,
    minAmount: 120,
    maxUses: 50,
    used: 50,
    status: "Expired",
    startDate: "2024-02-01",
    endDate: "2024-02-29",
    audience: "All Customers",
    description: "$20 off deep cleaning services"
  }
];

const StatusTypes = {
  ALL: 'all',
  ACTIVE: 'Active',
  SCHEDULED: 'Scheduled',
  EXPIRED: 'Expired',
  PAUSED: 'Paused'
};

const PromotionTypes = {
  ALL: 'all',
  PERCENTAGE: 'Percentage',
  FIXED: 'Fixed'
};

export default function Promotions() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPromotions, setSelectedPromotions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [statusFilter, setStatusFilter] = useState(StatusTypes.ALL);
  const [typeFilter, setTypeFilter] = useState(PromotionTypes.ALL);
  const [promotions, setPromotions] = useState(initialPromotions); // Add this line
  const [createModalOpen, setCreateModalOpen] = useState(false); // Add this for modal

  const filteredPromotions = promotions.filter(promotion => { // Change from mockPromotions to promotions
    const matchesSearch = promotion.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         promotion.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         promotion.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === StatusTypes.ALL || promotion.status === statusFilter;
    const matchesType = typeFilter === PromotionTypes.ALL || promotion.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Add the handleCreatePromotion function
  const handleCreatePromotion = (promotionData) => {
    const newPromotion = {
      id: Math.max(...promotions.map(p => p.id)) + 1,
      name: promotionData.name,
      code: promotionData.code,
      type: promotionData.type === 'percentage' ? 'Percentage' : 'Fixed',
      discount: parseInt(promotionData.discountValue),
      minAmount: parseInt(promotionData.minOrderAmount) || 0,
      maxUses: parseInt(promotionData.maxUses) || 1000,
      used: 0,
      status: "Active",
      startDate: promotionData.startDate || new Date().toISOString().split('T')[0],
      endDate: promotionData.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      audience: promotionData.audience === 'all' ? 'All Customers' : 
                promotionData.audience === 'new' ? 'New Customers' : 'Existing Customers',
      description: promotionData.description
    };

    setPromotions(prev => [...prev, newPromotion]);
    setCreateModalOpen(false);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedPromotions(filteredPromotions.map(p => p.id));
    } else {
      setSelectedPromotions([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedPromotions.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedPromotions, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedPromotions.slice(1));
    } else if (selectedIndex === selectedPromotions.length - 1) {
      newSelected = newSelected.concat(selectedPromotions.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedPromotions.slice(0, selectedIndex),
        selectedPromotions.slice(selectedIndex + 1),
      );
    }
    setSelectedPromotions(newSelected);
  };

  const isSelected = (id) => selectedPromotions.indexOf(id) !== -1;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Scheduled': return 'warning';
      case 'Expired': return 'error';
      case 'Paused': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <ActiveIcon />;
      case 'Scheduled': return <ScheduleIcon />;
      case 'Expired': return <PauseIcon />;
      case 'Paused': return <PauseIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const calculateUsage = (used, maxUses) => {
    return maxUses > 0 ? (used / maxUses) * 100 : 0;
  };

  const stats = [
    { label: 'Active Promotions', value: promotions.filter(p => p.status === 'Active').length, change: '+2' },
    { label: 'Total Redemptions', value: promotions.reduce((sum, p) => sum + p.used, 0), change: '+15%' },
    { label: 'Total Discount', value: '$8,450', change: '+12%' },
    { label: 'Avg. Usage Rate', value: '68%', change: '+5%' },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Promotions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Create Promotion
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage discount codes, special offers, and promotional campaigns
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Chip 
                  label={stat.change} 
                  size="small"
                  color={stat.change.startsWith('+') ? "success" : "error"}
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls Section */}
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search promotions..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Filters
            </Button>

            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={() => setFilterMenuAnchor(null)}
            >
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Status:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[StatusTypes.ALL, StatusTypes.ACTIVE, StatusTypes.SCHEDULED, StatusTypes.EXPIRED, StatusTypes.PAUSED].map(status => (
                    <Chip
                      key={status}
                      label={status}
                      size="small"
                      color={statusFilter === status ? "primary" : "default"}
                      onClick={() => setStatusFilter(status)}
                    />
                  ))}
                </Box>
              </MenuItem>
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Type:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[PromotionTypes.ALL, PromotionTypes.PERCENTAGE, PromotionTypes.FIXED].map(type => (
                    <Chip
                      key={type}
                      label={type}
                      size="small"
                      color={typeFilter === type ? "primary" : "default"}
                      onClick={() => setTypeFilter(type)}
                    />
                  ))}
                </Box>
              </MenuItem>
            </Menu>

            <Box sx={{ flexGrow: 1 }} />

            <IconButton sx={{ borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>

            {selectedPromotions.length > 0 && (
              <Chip 
                label={`${selectedPromotions.length} selected`} 
                color="primary" 
                variant="outlined"
                onDelete={() => setSelectedPromotions([])}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Promotions Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedPromotions.length > 0 && selectedPromotions.length < filteredPromotions.length}
                    checked={filteredPromotions.length > 0 && selectedPromotions.length === filteredPromotions.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Promotion</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Discount</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Usage</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Audience</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Validity</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPromotions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((promotion) => {
                  const isItemSelected = isSelected(promotion.id);
                  const usagePercentage = calculateUsage(promotion.used, promotion.maxUses);
                  
                  return (
                    <TableRow
                      key={promotion.id}
                      hover
                      selected={isItemSelected}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, promotion.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">
                            {promotion.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={promotion.code} 
                              size="small"
                              color="primary"
                              icon={<OfferIcon />}
                              onDelete={() => navigator.clipboard.writeText(promotion.code)}
                              deleteIcon={<CopyIcon />}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {promotion.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {promotion.type === 'Percentage' ? `${promotion.discount}%` : `$${promotion.discount}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Min: ${promotion.minAmount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">
                              {promotion.used}/{promotion.maxUses}
                            </Typography>
                            <Typography variant="body2" fontWeight="600">
                              {usagePercentage.toFixed(0)}%
                            </Typography>
                          </Box>
                          <Box 
                            sx={{ 
                              width: '100%', 
                              height: 6, 
                              backgroundColor: theme.palette.grey[200],
                              borderRadius: 3,
                              overflow: 'hidden'
                            }}
                          >
                            <Box 
                              sx={{ 
                                width: `${usagePercentage}%`, 
                                height: '100%',
                                backgroundColor: usagePercentage > 80 ? theme.palette.error.main : 
                                              usagePercentage > 50 ? theme.palette.warning.main : theme.palette.success.main,
                                transition: 'width 0.3s'
                              }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={promotion.audience} 
                          size="small"
                          variant="outlined"
                          icon={<PeopleIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(promotion.startDate).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          to {new Date(promotion.endDate).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={promotion.status}
                          size="small"
                          color={getStatusColor(promotion.status)}
                          icon={getStatusIcon(promotion.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPromotions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Card>

      {/* Add Create Promotion Modal - you'll need to import and use the modal component */}

      <CreatePromotionModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleCreatePromotion}
      />
    </Box>
  );
}